import {Extension} from '@tiptap/core';
import marked, {Renderer as BaseRenderer, type Tokens} from '@time-webkit/marked';
import {reportErrorToSentry} from 'utils/sentry';

// Renderer для корректного отображения сообщения, после добавления отрисовки переносов в marked.
// https://gitlab.tcsbank.ru/messenger/webapp/-/merge_requests/917
// https://jira.tcsbank.ru/browse/TIME-6147
class Renderer extends BaseRenderer {
    public code(token: Tokens.Code) {
        return super.code(token).trimEnd();
    }

    public blockquote({tokens}: Tokens.Blockquote) {
        const body = this.parser.parse(tokens);
        return `<blockquote>${body}</blockquote>`;
    }

    public paragraph({tokens}: Tokens.Paragraph) {
        return this.parser.parseInline(tokens).split('\n').map((i) => `<p>${i}</p>`).join('');
    }

    public heading(token: Tokens.Heading) {
        const {depth} = token;

        if (depth <= 3) {
            return super.heading(token).trimEnd();
        }

        return `<p>${'#'.repeat(depth)} ${this.parser.parseInline(token.tokens)}</p>`;
    }

    public hr(token: Tokens.Hr) {
        return super.hr(token).trimEnd();
    }

    public list(token: Tokens.List) {
        const ordered = token.ordered;
        const start = token.start;
        let body = '';

        for (const item of token.items) {
            body += this.listitem(item);
        }

        const type = ordered ? 'ol' : 'ul';
        const startAttr = (ordered && start !== 1) ? (' start="' + start + '"') : '';

        return '<' + type + startAttr + '>' + body + '</' + type + '>';
    }

    public listitem(token: Tokens.ListItem) {
        return super.listitem(token).trimEnd();
    }

    public extendedbr() {
        return '<p></p>';
    }

    public text(token: Tokens.Text | Tokens.Escape) {
        // При копировании пути файла на Windows теряется знак \, если после него идет !
        // https://jira.tcsbank.ru/browse/TIME-11743
        if (token.type === 'escape' && token.text === '!') {
            return '\\!';
        }

        return super.text(token);
    }
}

export const parseHtmlFromMarkdown = (markdown: string) => marked.parse(markdown, {
    async: false,
    renderer: new Renderer({
        breaks: true,
    }),
});

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        markdown: {
            insertMarkdown: (mrkdwn: string) => ReturnType;
            setMarkdown: (mrkdwn: string, emitUpdate: boolean) => ReturnType;
        };
    }
}

export const TimeWebkitMarkdownExtension = Extension.create({
    name: 'timeWebkitMarkdown',

    addCommands() {
        return {
            insertMarkdown: (inputMarkdown: string) => ({commands}) => {
                const html = parseHtmlFromMarkdown(inputMarkdown);

                try {
                    return commands.insertContent(html, {parseOptions: {preserveWhitespace: true}});
                } catch (err) {
                    reportErrorToSentry(new Error(`Failed to parse markdown: ${inputMarkdown}`, {cause: err}));
                }

                return false;
            },
            setMarkdown: (inputMarkdown: string, emitUpdate: boolean) => ({commands}) => {
                const html = parseHtmlFromMarkdown(inputMarkdown);

                try {
                    return commands.setContent(html, emitUpdate, {preserveWhitespace: true});
                } catch (err) {
                    reportErrorToSentry(new Error(`Failed to parse markdown: ${inputMarkdown}`, {cause: err}));
                }

                return false;
            },
        };
    },
});
